import { apiRequest } from '@/integrations/api/client';
import type { AppRole } from '@/integrations/api/auth_api';

export interface TenantBranding {
  tenant_id: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  accent_color: string | null;
  font_family: string | null;
}

export interface TenantModuleRow {
  module_key: string;
  enabled: boolean;
}

export interface PermissionCatalogItem {
  key: string;
  label: string;
  module: string;
}

export interface IamAuditEvent {
  id: string;
  tenant_id: string | null;
  actor_uuid: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export async function fetchTenantBranding(tenantId: string) {
  const res = await apiRequest<{ branding: TenantBranding }>(`/api/tenants/${tenantId}/branding`, {
    tenantId,
  });
  return res.branding;
}

export async function updateTenantBranding(
  tenantId: string,
  patch: Partial<{
    logo_url: string | null;
    primary_color: string;
    secondary_color: string;
    accent_color: string;
    font_family: string;
  }>,
) {
  const res = await apiRequest<{ branding: TenantBranding }>(`/api/tenants/${tenantId}/branding`, {
    method: 'PATCH',
    tenantId,
    body: patch,
  });
  return res.branding;
}

export async function fetchTenantModules(tenantId: string) {
  const res = await apiRequest<{
    modules: TenantModuleRow[];
    onboarding: {
      current_step: number;
      completed_steps: number[];
      is_complete: boolean;
      step: string;
      updated_at: string | null;
    };
  }>(`/api/tenants/${tenantId}/modules`, { tenantId });
  return res;
}

export async function updateTenantModules(
  tenantId: string,
  body: { modules?: TenantModuleRow[]; onboarding_step?: string },
) {
  return apiRequest(`/api/tenants/${tenantId}/modules`, {
    method: 'PATCH',
    tenantId,
    body,
  });
}

export async function fetchPermissionCatalog() {
  const res = await apiRequest<{ permissions: PermissionCatalogItem[] }>(
    '/api/tenants/permissions/catalog',
  );
  return res.permissions;
}

export async function fetchRolePermissions(tenantId: string) {
  const res = await apiRequest<{ roles: Record<string, string[]> }>(
    `/api/tenants/${tenantId}/permissions/roles`,
    { tenantId },
  );
  return res.roles;
}

export async function setRolePermissions(tenantId: string, role: AppRole, permissions: string[]) {
  return apiRequest(`/api/tenants/${tenantId}/permissions/roles/${role}`, {
    method: 'PUT',
    tenantId,
    body: { permissions },
  });
}

export async function fetchIamAudit(tenantId: string) {
  const res = await apiRequest<{ events: IamAuditEvent[] }>(`/api/tenants/${tenantId}/audit`, {
    tenantId,
  });
  return res.events;
}
